// GAVION sequence-performance: the « Performance » view of a sequence. Typed shape of public.sequence_performance
// (supabase/schemas/103_gavion_sequence_performance.sql, jsonb) and the pure readings the page shows: the jsonb
// normaliser, the best / worst step, the small-sample rule and the plain-French « Conclusions ».
import { ADDRESS_KINDS } from "./emailAddressKind";

/** A rate rests on fewer sends (or contacted leads) than this: flagged, and never used for a conclusion. */
export const MIN_SAMPLE = 30;

/** Percentage with one decimal, null when its base is 0. */
export type Rate = number | null;

export type PerformanceTotals = {
  enrolled: number;
  active: number;
  completed: number;
  stopped: number;
  stopReasons: Record<string, number>;
  /** Enrolments that received at least one email. */
  contacted: number;
  sent: number;
  /** Human opens (likely automatic opens are in openedAuto, never here). */
  opened: number;
  openedAuto: number;
  clicked: number;
  replied: number;
  unsubscribed: number;
  bounced: number;
  complained: number;
  openRateOnSent: Rate;
  clickRateOnSent: Rate;
  replyRateOnSent: Rate;
  unsubscribeRateOnSent: Rate;
  bounceRateOnSent: Rate;
  complaintRateOnSent: Rate;
  enrollmentsOpened: number;
  enrollmentsClicked: number;
  enrollmentsReplied: number;
  enrollmentsUnsubscribed: number;
  enrollmentsBounced: number;
  openRateOnContacted: Rate;
  clickRateOnContacted: Rate;
  replyRateOnContacted: Rate;
  unsubscribeRateOnContacted: Rate;
  bounceRateOnContacted: Rate;
  contactedRateOnEnrolled: Rate;
};

/** Per compagnie enrolled, what happened after its enrolment (stages are cumulative: a customer is in pipeline…). */
export type PerformanceOutcomes = {
  companies: number;
  opened: number;
  clicked: number;
  replied: number;
  demoBooked: number;
  inPipeline: number;
  notInterested: number;
  dealCreated: number;
  dealWon: number;
  customer: number;
};

export type PerformanceStep = {
  step: number;
  templateId: number | null;
  templateName: string | null;
  subject: string | null;
  sent: number;
  opened: number;
  openedAuto: number;
  clicked: number;
  replied: number;
  unsubscribed: number;
  bounced: number;
  complained: number;
  openRate: Rate;
  clickRate: Rate;
  replyRate: Rate;
  unsubscribeRate: Rate;
  bounceRate: Rate;
};

/** One value of a breakdown; rates are on contacted enrolments. key null = not given. */
export type BreakdownRow = {
  key: string | null;
  enrolled: number;
  contacted: number;
  sent: number;
  opened: number;
  replied: number;
  /** Reached the demo stage (or further) after the enrolment. */
  positive: number;
  openRate: Rate;
  replyRate: Rate;
  positiveRate: Rate;
};

export const BREAKDOWN_DIMS = [
  "addressKind",
  "sector",
  "region",
  "city",
  "weekday",
  "hour",
  "openDelay",
] as const;
export type BreakdownDim = (typeof BREAKDOWN_DIMS)[number];
export const OPEN_DELAY_BUCKETS = [
  "lt_1h",
  "h1_24",
  "d1_3",
  "gt_3d",
  "not_opened",
] as const;

export type TimelineDay = {
  /** Toronto date, YYYY-MM-DD. */
  day: string;
  sent: number;
  opened: number;
  replied: number;
};

export const EVENT_KINDS = [
  "open",
  "click",
  "reply",
  "unsubscribe",
  "bounce",
  "complaint",
] as const;
export type PerformanceEventKind = (typeof EVENT_KINDS)[number];
export type PerformanceEvent = {
  kind: PerformanceEventKind;
  at: string;
  companyId: number | null;
  companyName: string | null;
  step: number | null;
};

export type SequencePerformance = {
  sequenceId: number;
  generatedAt: string | null;
  totals: PerformanceTotals;
  outcomes: PerformanceOutcomes;
  steps: PerformanceStep[];
  breakdowns: Record<BreakdownDim, BreakdownRow[]>;
  timeline: TimelineDay[];
  recent: PerformanceEvent[];
};

// ---------------------------------------------------------------- normaliser
type Obj = Record<string, unknown>;
const obj = (v: unknown): Obj =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Obj) : {};
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const num = (v: unknown): number => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};
const rate = (v: unknown): Rate => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const idOrNull = (v: unknown): number | null =>
  v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v);
const strOrNull = (v: unknown): string | null => (v == null ? null : String(v));

const breakdownRow = (v: unknown): BreakdownRow => {
  const r = obj(v);
  return {
    key: strOrNull(r.key),
    enrolled: num(r.enrolled),
    contacted: num(r.contacted),
    sent: num(r.sent),
    opened: num(r.opened),
    replied: num(r.replied),
    positive: num(r.positive),
    openRate: rate(r.open_rate_on_contacted),
    replyRate: rate(r.reply_rate_on_contacted),
    positiveRate: rate(r.positive_rate_on_contacted),
  };
};

const SQL_DIMS: Record<BreakdownDim, string> = {
  addressKind: "address_kind",
  sector: "sector",
  region: "region",
  city: "city",
  weekday: "weekday",
  hour: "hour",
  openDelay: "open_delay",
};

/** The RPC's jsonb (bigints may come as strings) → typed; anything missing is 0, [] or null. */
export function normalizeSequencePerformance(
  raw: unknown,
  sequenceId?: number,
): SequencePerformance {
  const r = obj(raw);
  const t = obj(r.totals);
  const o = obj(r.outcomes);
  const b = obj(r.breakdowns);
  const reasons = obj(t.stop_reasons);
  return {
    sequenceId: idOrNull(r.sequence_id) ?? sequenceId ?? 0,
    generatedAt: strOrNull(r.generated_at),
    totals: {
      enrolled: num(t.enrolled),
      active: num(t.active),
      completed: num(t.completed),
      stopped: num(t.stopped),
      stopReasons: Object.fromEntries(
        Object.entries(reasons).map(([k, v]) => [k, num(v)]),
      ),
      contacted: num(t.contacted),
      sent: num(t.sent),
      opened: num(t.opened),
      openedAuto: num(t.opened_auto),
      clicked: num(t.clicked),
      replied: num(t.replied),
      unsubscribed: num(t.unsubscribed),
      bounced: num(t.bounced),
      complained: num(t.complained),
      openRateOnSent: rate(t.open_rate_on_sent),
      clickRateOnSent: rate(t.click_rate_on_sent),
      replyRateOnSent: rate(t.reply_rate_on_sent),
      unsubscribeRateOnSent: rate(t.unsubscribe_rate_on_sent),
      bounceRateOnSent: rate(t.bounce_rate_on_sent),
      complaintRateOnSent: rate(t.complaint_rate_on_sent),
      enrollmentsOpened: num(t.enrollments_opened),
      enrollmentsClicked: num(t.enrollments_clicked),
      enrollmentsReplied: num(t.enrollments_replied),
      enrollmentsUnsubscribed: num(t.enrollments_unsubscribed),
      enrollmentsBounced: num(t.enrollments_bounced),
      openRateOnContacted: rate(t.open_rate_on_contacted),
      clickRateOnContacted: rate(t.click_rate_on_contacted),
      replyRateOnContacted: rate(t.reply_rate_on_contacted),
      unsubscribeRateOnContacted: rate(t.unsubscribe_rate_on_contacted),
      bounceRateOnContacted: rate(t.bounce_rate_on_contacted),
      contactedRateOnEnrolled: rate(t.contacted_rate_on_enrolled),
    },
    outcomes: {
      companies: num(o.companies),
      opened: num(o.opened),
      clicked: num(o.clicked),
      replied: num(o.replied),
      demoBooked: num(o.demo_booked),
      inPipeline: num(o.in_pipeline),
      notInterested: num(o.not_interested),
      dealCreated: num(o.deal_created),
      dealWon: num(o.deal_won),
      customer: num(o.customer),
    },
    steps: arr(r.steps)
      .map((v): PerformanceStep => {
        const s = obj(v);
        return {
          step: num(s.step),
          templateId: idOrNull(s.template_id),
          templateName: strOrNull(s.template_name),
          subject: strOrNull(s.subject),
          sent: num(s.sent),
          opened: num(s.opened),
          openedAuto: num(s.opened_auto),
          clicked: num(s.clicked),
          replied: num(s.replied),
          unsubscribed: num(s.unsubscribed),
          bounced: num(s.bounced),
          complained: num(s.complained),
          openRate: rate(s.open_rate_on_sent),
          clickRate: rate(s.click_rate_on_sent),
          replyRate: rate(s.reply_rate_on_sent),
          unsubscribeRate: rate(s.unsubscribe_rate_on_sent),
          bounceRate: rate(s.bounce_rate_on_sent),
        };
      })
      .sort((a, b2) => a.step - b2.step),
    breakdowns: Object.fromEntries(
      BREAKDOWN_DIMS.map((d) => [d, arr(b[SQL_DIMS[d]]).map(breakdownRow)]),
    ) as Record<BreakdownDim, BreakdownRow[]>,
    timeline: arr(r.timeline)
      .map((v) => {
        const d = obj(v);
        return {
          day: String(d.day ?? "").slice(0, 10),
          sent: num(d.sent),
          opened: num(d.opened),
          replied: num(d.replied),
        };
      })
      .filter((d) => d.day !== "")
      .sort((a, b2) => a.day.localeCompare(b2.day)),
    recent: arr(r.recent).flatMap((v): PerformanceEvent[] => {
      const e = obj(v);
      const kind = String(e.kind ?? "") as PerformanceEventKind;
      if (!EVENT_KINDS.includes(kind) || e.at == null) return [];
      return [
        {
          kind,
          at: String(e.at),
          companyId: idOrNull(e.company_id),
          companyName: strOrNull(e.company_name),
          step: idOrNull(e.step),
        },
      ];
    }),
  };
}

// ---------------------------------------------------------------- readings
/** True when a rate rests on fewer than MIN_SAMPLE sends / contacted leads (shown flagged, never concluded on). */
export const smallSample = (base: number): boolean => base < MIN_SAMPLE;

const byReplyThenOpen = (
  a: { replyRate: Rate; openRate: Rate },
  b: { replyRate: Rate; openRate: Rate },
) =>
  (b.replyRate ?? -1) - (a.replyRate ?? -1) ||
  (b.openRate ?? -1) - (a.openRate ?? -1);

/**
 * The best and worst step on reply rate, then open rate, among the steps that sent something (at least two). Null
 * when they cannot be told apart (one step, or all equal).
 */
export function bestAndWorstSteps(steps: readonly PerformanceStep[]): {
  best: number | null;
  worst: number | null;
} {
  const ranked = steps.filter((s) => s.sent > 0).sort(byReplyThenOpen);
  if (ranked.length < 2) return { best: null, worst: null };
  const first = ranked[0];
  const last = ranked[ranked.length - 1];
  if (byReplyThenOpen(first, last) === 0) return { best: null, worst: null };
  return { best: first.step, worst: last.step };
}

export const CONCLUSION_KEYS = [
  "small_sample",
  "nominative_better",
  "nominative_only",
  "generic_better",
  "generic_only",
  "best_step_reply",
  "best_step_open",
  "worst_step",
  "step_unsubscribes",
  "high_bounce",
  "low_open",
  "opens_no_replies",
  "best_sector",
  "best_weekday",
  "best_hour",
  "demos",
] as const;
/** A plain-French (or English) reading: an i18n key under gavion.email.performance.conclusions and its values. */
export type Conclusion = {
  key: (typeof CONCLUSION_KEYS)[number];
  params: Record<string, string | number>;
  tone: "good" | "bad" | "info";
};
export type ConclusionLabels = {
  step?: (s: PerformanceStep) => string;
  sector?: (key: string) => string;
  weekday?: (key: string) => string;
  hour?: (key: string) => string;
};

const ratio = (a: number, b: number) => Math.round((10 * a) / b) / 10;

/** The value with the best reply rate of a breakdown, when at least two values have MIN_SAMPLE contacted leads. */
const bestOf = (rows: readonly BreakdownRow[]) => {
  const ok = rows
    .filter((r) => r.key != null && !smallSample(r.contacted))
    .sort(byReplyThenOpen);
  if (ok.length < 2 || byReplyThenOpen(ok[0], ok[ok.length - 1]) === 0)
    return null;
  return ok[0];
};

/**
 * Automatic readings of the campaign, strongest first. Only groups with at least MIN_SAMPLE sends (steps, totals) or
 * contacted leads (breakdowns) are read; below that, one « small_sample » reading says so.
 */
export function performanceConclusions(
  p: SequencePerformance,
  labels: ConclusionLabels = {},
): Conclusion[] {
  const out: Conclusion[] = [];
  const stepLabel =
    labels.step ?? ((s: PerformanceStep) => s.templateName ?? `#${s.step}`);
  const t = p.totals;
  if (smallSample(t.sent))
    out.push({
      key: "small_sample",
      params: { sent: t.sent, min: MIN_SAMPLE },
      tone: "info",
    });

  // address type: a person's address vs a shared mailbox
  const kinds = new Map(
    p.breakdowns.addressKind.map((r) => [r.key ?? "", r] as const),
  );
  const nom = kinds.get(ADDRESS_KINDS[0]);
  const gen = kinds.get(ADDRESS_KINDS[1]);
  if (
    nom &&
    gen &&
    !smallSample(nom.contacted) &&
    !smallSample(gen.contacted)
  ) {
    const n = nom.replyRate ?? 0;
    const g = gen.replyRate ?? 0;
    if (n > 0 && g === 0)
      out.push({
        key: "nominative_only",
        params: { rate: n },
        tone: "good",
      });
    else if (g > 0 && n === 0)
      out.push({ key: "generic_only", params: { rate: g }, tone: "info" });
    else if (n > 0 && g > 0 && n / g >= 1.5)
      out.push({
        key: "nominative_better",
        params: { ratio: ratio(n, g) },
        tone: "good",
      });
    else if (n > 0 && g > 0 && g / n >= 1.5)
      out.push({
        key: "generic_better",
        params: { ratio: ratio(g, n) },
        tone: "info",
      });
  }

  // steps with enough sends
  const steps = p.steps.filter((s) => !smallSample(s.sent));
  const { best, worst } = bestAndWorstSteps(steps);
  const stepOf = (n: number | null) => steps.find((s) => s.step === n);
  const b = stepOf(best);
  if (b && (b.replyRate ?? 0) > 0)
    out.push({
      key: "best_step_reply",
      params: { step: b.step, name: stepLabel(b), rate: b.replyRate ?? 0 },
      tone: "good",
    });
  else if (b)
    out.push({
      key: "best_step_open",
      params: { step: b.step, name: stepLabel(b), rate: b.openRate ?? 0 },
      tone: "good",
    });
  const w = stepOf(worst);
  if (w)
    out.push({
      key: "worst_step",
      params: {
        step: w.step,
        name: stepLabel(w),
        reply: w.replyRate ?? 0,
        open: w.openRate ?? 0,
      },
      tone: "bad",
    });
  const churn = [...steps]
    .filter((s) => s.unsubscribed >= 2 && (s.unsubscribeRate ?? 0) >= 2)
    .sort(
      (a, c) =>
        (c.unsubscribeRate ?? 0) - (a.unsubscribeRate ?? 0) ||
        c.unsubscribed - a.unsubscribed,
    )[0];
  if (churn)
    out.push({
      key: "step_unsubscribes",
      params: {
        step: churn.step,
        name: stepLabel(churn),
        count: churn.unsubscribed,
        rate: churn.unsubscribeRate ?? 0,
      },
      tone: "bad",
    });

  // the whole campaign
  if (!smallSample(t.sent)) {
    if ((t.bounceRateOnSent ?? 0) >= 5)
      out.push({
        key: "high_bounce",
        params: { rate: t.bounceRateOnSent ?? 0, count: t.bounced },
        tone: "bad",
      });
    if ((t.openRateOnSent ?? 0) < 15)
      out.push({
        key: "low_open",
        params: { rate: t.openRateOnSent ?? 0 },
        tone: "bad",
      });
    else if (
      (t.openRateOnContacted ?? 0) >= 40 &&
      (t.replyRateOnContacted ?? 0) < 2
    )
      out.push({
        key: "opens_no_replies",
        params: {
          open: t.openRateOnContacted ?? 0,
          reply: t.replyRateOnContacted ?? 0,
        },
        tone: "bad",
      });
  }

  // who and when
  const sector = bestOf(p.breakdowns.sector);
  if (sector?.key != null)
    out.push({
      key: "best_sector",
      params: {
        sector: labels.sector?.(sector.key) ?? sector.key,
        rate: sector.replyRate ?? 0,
      },
      tone: "info",
    });
  const day = bestOf(p.breakdowns.weekday);
  if (day?.key != null)
    out.push({
      key: "best_weekday",
      params: {
        day: labels.weekday?.(day.key) ?? day.key,
        rate: day.replyRate ?? 0,
        open: day.openRate ?? 0,
      },
      tone: "info",
    });
  const hour = bestOf(p.breakdowns.hour);
  if (hour?.key != null)
    out.push({
      key: "best_hour",
      params: {
        hour: labels.hour?.(hour.key) ?? hour.key,
        rate: hour.replyRate ?? 0,
        open: hour.openRate ?? 0,
      },
      tone: "info",
    });

  // what it brought
  if (p.outcomes.demoBooked > 0)
    out.push({
      key: "demos",
      params: {
        smart_count: p.outcomes.demoBooked,
        companies: p.outcomes.companies,
        won: p.outcomes.dealWon,
      },
      tone: "good",
    });
  return out;
}

// ---------------------------------------------------------------- demo data (FakeRest)
/** A deterministic, plausible performance for the demo (seeded by the sequence id), in the RPC's jsonb shape. */
export function fakeSequencePerformanceJson(
  sequenceId: number,
  steps: readonly {
    step: number;
    templateId: number | null;
    templateName: string | null;
    subject: string | null;
  }[],
  now: Date,
): Record<string, unknown> {
  let seed = (Math.abs(Math.trunc(sequenceId)) % 997) + 1;
  const rnd = () => {
    seed = (seed * 16807) % 2147483647;
    return (seed - 1) / 2147483646;
  };
  const pct = (n: number, d: number) =>
    d > 0 ? Math.round((1000 * n) / d) / 10 : null;
  const pick = (n: number, p: number) => Math.round(n * p);
  if (steps.length === 0)
    return {
      sequence_id: sequenceId,
      generated_at: now.toISOString(),
      totals: {},
      outcomes: {},
      steps: [],
      breakdowns: {},
      timeline: [],
      recent: [],
    };

  const enrolled = 80 + Math.floor(rnd() * 80);
  const contacted = enrolled - 6;
  let reach = contacted;
  const stepRows = steps.map((s, i) => {
    const sent = i === 0 ? contacted : pick(reach, 0.78);
    reach = sent;
    const opened = pick(sent, 0.32 + rnd() * 0.2);
    const replied = pick(sent, 0.015 + rnd() * 0.05);
    const unsubscribed = pick(sent, rnd() * 0.03);
    const bounced = i === 0 ? pick(sent, 0.03) : 0;
    const clicked = pick(opened, 0.12);
    return {
      step: s.step,
      template_id: s.templateId,
      template_name: s.templateName,
      subject: s.subject,
      sent,
      opened,
      opened_auto: pick(sent, 0.1),
      clicked,
      replied,
      unsubscribed,
      bounced,
      complained: 0,
      open_rate_on_sent: pct(opened, sent),
      click_rate_on_sent: pct(clicked, sent),
      reply_rate_on_sent: pct(replied, sent),
      unsubscribe_rate_on_sent: pct(unsubscribed, sent),
      bounce_rate_on_sent: pct(bounced, sent),
    };
  });
  const sum = (k: keyof (typeof stepRows)[number]) =>
    stepRows.reduce((a, s) => a + Number(s[k] ?? 0), 0);
  const sent = sum("sent");
  const replied = sum("replied");
  const unsub = sum("unsubscribed");
  const bounced = sum("bounced");
  const eOpened = Math.min(contacted, pick(contacted, 0.55));
  const eClicked = pick(eOpened, 0.15);
  const demo = Math.max(1, pick(replied, 0.4));
  const pipeline = Math.max(0, demo - 1);
  const won = Math.min(pipeline, 1);
  const row = (key: string | null, share: number, boost: number) => {
    const en = Math.max(1, pick(enrolled, share));
    const co = Math.max(1, pick(contacted, share));
    const op = Math.min(co, pick(co, 0.5 * boost));
    const re = Math.min(op, pick(co, 0.04 * boost));
    const po = Math.min(re, pick(co, 0.015 * boost));
    return {
      key,
      enrolled: en,
      contacted: co,
      sent: pick(sent, share),
      opened: op,
      replied: re,
      positive: po,
      open_rate_on_contacted: pct(op, co),
      reply_rate_on_contacted: pct(re, co),
      positive_rate_on_contacted: pct(po, co),
    };
  };
  const day0 = new Date(now.getTime() - 20 * 86_400_000);
  const timeline = Array.from({ length: 21 }, (_, i) => {
    const d = new Date(day0.getTime() + i * 86_400_000);
    const weekend = d.getUTCDay() === 0 || d.getUTCDay() === 6;
    return {
      day: d.toISOString().slice(0, 10),
      sent: weekend ? 0 : Math.round(sent / 15),
      opened: Math.round((sent / 15) * 0.4 * (0.6 + rnd())),
      replied: rnd() < 0.3 ? 1 : 0,
    };
  });
  const kinds = ["open", "open", "click", "reply", "open", "unsubscribe"];
  const recent = Array.from({ length: 12 }, (_, i) => ({
    kind: kinds[i % kinds.length],
    at: new Date(now.getTime() - (i + 1) * 3_600_000 * 5).toISOString(),
    company_id: 1 + ((sequenceId * 7 + i * 13) % 50),
    company_name: `Compagnie démo ${i + 1}`,
    step: steps[i % steps.length].step,
  }));
  return {
    sequence_id: sequenceId,
    generated_at: now.toISOString(),
    totals: {
      enrolled,
      active: 18,
      completed: Math.max(0, enrolled - 18 - (replied + unsub + bounced)),
      stopped: replied + unsub + bounced,
      stop_reasons: {
        reply: replied,
        unsubscribe: unsub,
        bounce: bounced,
      },
      contacted,
      sent,
      opened: sum("opened"),
      opened_auto: sum("opened_auto"),
      clicked: sum("clicked"),
      replied,
      unsubscribed: unsub,
      bounced,
      complained: 0,
      open_rate_on_sent: pct(sum("opened"), sent),
      click_rate_on_sent: pct(sum("clicked"), sent),
      reply_rate_on_sent: pct(replied, sent),
      unsubscribe_rate_on_sent: pct(unsub, sent),
      bounce_rate_on_sent: pct(bounced, sent),
      complaint_rate_on_sent: pct(0, sent),
      enrollments_opened: eOpened,
      enrollments_clicked: eClicked,
      enrollments_replied: replied,
      enrollments_unsubscribed: unsub,
      enrollments_bounced: bounced,
      open_rate_on_contacted: pct(eOpened, contacted),
      click_rate_on_contacted: pct(eClicked, contacted),
      reply_rate_on_contacted: pct(replied, contacted),
      unsubscribe_rate_on_contacted: pct(unsub, contacted),
      bounce_rate_on_contacted: pct(bounced, contacted),
      contacted_rate_on_enrolled: pct(contacted, enrolled),
    },
    outcomes: {
      companies: enrolled,
      opened: eOpened,
      clicked: eClicked,
      replied,
      demo_booked: demo,
      in_pipeline: pipeline,
      not_interested: Math.max(1, pick(replied, 0.3)),
      deal_created: pipeline,
      deal_won: won,
      customer: won,
    },
    steps: stepRows,
    breakdowns: {
      address_kind: [
        row("nominative", 0.45, 1.8),
        row("generic", 0.4, 0.6),
        row("webmail", 0.15, 1.1),
      ],
      sector: [
        row("Plumber", 0.4, 1.2),
        row("HVAC", 0.3, 1),
        row("Roofer", 0.2, 0.8),
        row(null, 0.1, 0.5),
      ],
      region: [row("Montréal", 0.6, 1), row("Laval", 0.4, 1.1)],
      city: [row("Montréal", 0.5, 1), row("Laval", 0.3, 1.1)],
      weekday: [1, 2, 3, 4, 5].map((d) =>
        row(String(d), 0.2, d === 2 ? 1.4 : 0.9),
      ),
      hour: [9, 10, 11, 14].map((h) =>
        row(String(h), 0.25, h === 10 ? 1.3 : 0.9),
      ),
      open_delay: [
        row("lt_1h", 0.2, 1.6),
        row("h1_24", 0.2, 1.2),
        row("d1_3", 0.1, 0.8),
        row("gt_3d", 0.05, 0.5),
        row("not_opened", 0.45, 0),
      ],
    },
    timeline,
    recent,
  };
}
